import React from 'react';
import { motion } from 'framer-motion';
import { 
  Wifi,
  Battery,
  Thermometer,
  Gauge,
  Activity,
  Zap,
  Shield,
  Smartphone,
  Cloud,
  BarChart3,
  Settings,
  Download,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const Products = () => {
  const productFeatures = [
    {
      icon: Wifi,
      title: 'Wireless Connectivity',
      description: 'Long-range wireless communication with mesh networking capabilities'
    },
    {
      icon: Battery,
      title: 'Self-Sustaining Power',
      description: 'Solar charging with backup battery for continuous operation'
    },
    {
      icon: Thermometer,
      title: 'Temperature Monitoring',
      description: 'Precision temperature sensing with configurable thresholds'
    },
    {
      icon: Gauge,
      title: 'Vibration Analysis',
      description: 'Tri-axial vibration monitoring with FFT analysis'
    },
    {
      icon: Activity,
      title: 'Acoustic Monitoring',
      description: 'Sound pattern analysis for early fault detection'
    },
    {
      icon: Zap,
      title: 'Magnetic Flux Detection',
      description: 'Electromagnetic field monitoring for motor health'
    }
  ];

  const specifications = [
    { category: 'Physical', specs: [
      'Dimensions: 120mm x 80mm x 40mm',
      'Weight: 350g',
      'IP65 rated enclosure',
      'Operating temp: -40°C to +85°C',
      'Mounting: Magnetic or bolt-on'
    ]},
    { category: 'Sensors', specs: [
      'Temperature: ±0.1°C accuracy',
      'Vibration: 3-axis accelerometer',
      'Acoustic: 20Hz - 20kHz range',
      'Magnetic: ±2% accuracy',
      'Humidity: ±2% RH accuracy'
    ]},
    { category: 'Connectivity', specs: [
      'LoRaWAN, WiFi, 4G options',
      'Range: Up to 15km (LoRaWAN)',
      'Mesh networking capable',
      'AES-256 encryption',
      'Cloud integration ready'
    ]},
    { category: 'Power', specs: [
      'Solar panel: 5W',
      'Battery: 10,000mAh Li-ion',
      'Runtime: 5+ years (typical)',
      'Sleep mode: <10µA',
      'Energy harvesting capable'
    ]}
  ];

  const softwareFeatures = [
    'Real-time monitoring dashboard',
    'Predictive analytics engine',
    'Mobile app for iOS and Android',
    'API integration capabilities',
    'Custom alert configuration',
    'Historical data analysis',
    'Machine learning algorithms',
    'Cloud and on-premise deployment'
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F0F4FE] via-white to-[#E2EEFE] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
                Smart IoT Solutions for <span className="text-[#233790]">Industrial Monitoring</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Advanced wireless sensors and analytics platform designed for harsh industrial 
                environments, providing real-time insights into asset health and performance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="inline-flex items-center px-8 py-3 bg-[#233790] text-white font-medium rounded-lg hover:bg-[#1e2d73] transition-colors">
                  Request Demo
                </button>
                <button className="inline-flex items-center px-8 py-3 border border-[#233790] text-[#233790] font-medium rounded-lg hover:bg-[#F0F4FE] transition-colors">
                  <Download className="w-4 h-4 mr-2" />
                  Download Specs
                </button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative">
                <img
                  src="/images/x2.png"
                  alt="Tractian Calendar OS"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Advanced Monitoring Capabilities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Multi-sensor platform with intelligent analytics for comprehensive asset monitoring
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {productFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-[#233790] to-[#3BA6EA] rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Technical Specifications
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {specifications.map((spec, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{spec.category}</h3>
                <ul className="space-y-2">
                  {spec.specs.map((item, idx) => (
                    <li key={idx} className="text-sm text-gray-600 flex items-start">
                      <div className="w-1.5 h-1.5 bg-[#233790] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Software Platform */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-[#233790] to-[#3BA6EA] rounded-lg flex items-center justify-center mr-4">
                  <Cloud className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-light text-gray-900">Analytics Platform</h2>
              </div>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Comprehensive software suite that transforms raw sensor data into actionable insights, 
                featuring machine learning algorithms and predictive analytics capabilities.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {softwareFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-[#233790] mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img
                src="/images/X5.png"
                alt="Analytics Dashboard"
                className="w-full rounded-lg shadow-xl"
              />
              
            </motion.div>
          </div>
        </div>
      </section>

      {/* Deployment Options */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Flexible Deployment Options
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-lg p-8 text-center shadow-sm"
            >
              <Cloud className="w-16 h-16 text-[#233790] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Cloud-Based</h3>
              <p className="text-gray-600 mb-6">
                Fully managed cloud solution with automatic updates, scalability, and 24/7 monitoring.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li>• No infrastructure management</li>
                <li>• Automatic scaling</li>
                <li>• Global accessibility</li>
                <li>• Regular updates</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-lg p-8 text-center shadow-sm"
            >
              <Shield className="w-16 h-16 text-[#233790] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-4">On-Premise</h3>
              <p className="text-gray-600 mb-6">
                Private deployment within your infrastructure for maximum security and control.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li>• Complete data control</li>
                <li>• Enhanced security</li>
                <li>• Custom configurations</li>
                <li>• Compliance ready</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-lg p-8 text-center shadow-sm"
            >
              <Settings className="w-16 h-16 text-[#233790] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Hybrid</h3>
              <p className="text-gray-600 mb-6">
                Best of both worlds with edge computing and cloud analytics integration.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li>• Local processing</li>
                <li>• Cloud insights</li>
                <li>• Reduced latency</li>
                <li>• Flexible architecture</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Ready to Experience Smart Monitoring?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              See how our IoT solutions can transform your asset monitoring and 
              maintenance strategies with a personalized demonstration.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="inline-flex items-center px-8 py-3 bg-[#233790] text-white font-medium rounded-lg hover:bg-[#1e2d73] transition-colors">
                Schedule Demo
              </button>
              <button className="inline-flex items-center px-8 py-3 border border-[#233790] text-[#233790] font-medium rounded-lg hover:bg-[#F0F4FE] transition-colors">
                Contact Sales <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Products;